import React from 'react';
import { ChevronRight, Edit3, TrendingDown, XCircle, Loader2 } from 'lucide-react';
import { normalizeSymbol } from '../../../../../modules/symbolNormalizer';
import { formatNumber, getSideLabel, getNetSide } from './utils';

const PositionsTable = ({
  positions,
  filteredAndSorted,
  sortConfig,
  onSort,
  expandedSymbol,
  onToggleExpand,
  processing,
  onOpenCloseModal,
  onOpenModifyModal,
  onRemoveOrder,
  userOrders,
  formatNumber: fn,
}) => {
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <span className="pm-sort">↕</span>;
    return <span className="pm-sort">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
  };

  const isSorted = (key) => sortConfig.key === key;

  if (!positions || Object.keys(positions).length === 0) {
    return (
      <div className="pm-table-card">
        <div className="pm-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="44" height="44">
            <path d="M3 3v18h18" />
            <path d="M18 9l-5 5-4-4-3 3" />
          </svg>
          <p>No open positions found</p>
        </div>
      </div>
    );
  }

  if (filteredAndSorted.length === 0) {
    return (
      <div className="pm-table-card">
        <div className="pm-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="44" height="44">
            <path d="M21 21l-4.35-4.35" />
            <circle cx="11" cy="11" r="8" />
          </svg>
          <p>No positions match your search or filter</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pm-table-card">
      <div className="pm-table-head">
        <h3 className="pm-table-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
            <path d="M3 3v18h18" />
            <path d="M18 9l-5 5-4-4-3 3" />
          </svg>
          Instruments
        </h3>
        <span className="pm-table-count">
          {filteredAndSorted.length} of {Object.keys(positions).length}
        </span>
      </div>
      <div className="pm-table-wrap">
        <table className="pm-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th
                onClick={() => onSort('symbol')}
                className={isSorted('symbol') ? 'sorted' : ''}
              >
                Symbol {getSortIcon('symbol')}
              </th>
              <th
                onClick={() => onSort('netQuantity')}
                className={isSorted('netQuantity') ? 'sorted' : ''}
              >
                Net Qty {getSortIcon('netQuantity')}
              </th>
              <th>Side</th>
              <th
                onClick={() => onSort('totalBuyQuantity')}
                className={isSorted('totalBuyQuantity') ? 'sorted' : ''}
              >
                Long Qty {getSortIcon('totalBuyQuantity')}
              </th>
              <th
                onClick={() => onSort('totalSellQuantity')}
                className={isSorted('totalSellQuantity') ? 'sorted' : ''}
              >
                Short Qty {getSortIcon('totalSellQuantity')}
              </th>
              <th
                onClick={() => onSort('userCount')}
                className={isSorted('userCount') ? 'sorted' : ''}
              >
                Users {getSortIcon('userCount')}
              </th>
              <th>Avg Buy</th>
              <th>Avg Sell</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.map(([symbol, data]) => {
              const side = getNetSide(data.netQuantity);
              const isExpanded = expandedSymbol === symbol;
              const breakdown = data.userBreakdown || {};

              return (
                <React.Fragment key={symbol}>
                  {/* Main instrument row */}
                  <tr
                    className={'pm-row ' + (isExpanded ? 'pm-row--expanded' : '')}
                    onClick={() => onToggleExpand(symbol)}
                  >
                    <td>
                      <ChevronRight
                        className={'pm-expand-icon ' + (isExpanded ? 'pm-expand-icon--open' : '')}
                      />
                    </td>
                    <td>
                      <div className="pm-symbol">
                        <span className={'pm-symbol-dot ' + side} />
                        <span className="pm-symbol-name">{normalizeSymbol(symbol)}</span>
                      </div>
                    </td>
                    <td>
                      <span className={'pm-num ' + (data.netQuantity > 0 ? 'pm-num--positive' : data.netQuantity < 0 ? 'pm-num--negative' : 'pm-num--muted')}>
                        {data.netQuantity > 0 ? '+' : ''}{formatNumber(data.netQuantity, 2)}
                      </span>
                    </td>
                    <td>
                      <span className={'pm-side pm-side--' + side}>
                        {side === 'long' ? 'LONG' : side === 'short' ? 'SHORT' : 'FLAT'}
                      </span>
                    </td>
                    <td>
                      <span className="pm-num">{formatNumber(data.totalBuyQuantity, 2)}</span>
                    </td>
                    <td>
                      <span className="pm-num">{formatNumber(data.totalSellQuantity, 2)}</span>
                    </td>
                    <td>
                      <span className="pm-num pm-num--muted">{data.userCount || 0}</span>
                    </td>
                    <td className="pm-num pm-num--muted" style={{ fontSize: '0.76rem' }}>
                      {data.averageBuyPrice ? formatNumber(data.averageBuyPrice, 5) : '---'}
                    </td>
                    <td className="pm-num pm-num--muted" style={{ fontSize: '0.76rem' }}>
                      {data.averageSellPrice ? formatNumber(data.averageSellPrice, 5) : '---'}
                    </td>
                  </tr>

                  {/* Expanded detail: user breakdown */}
                  {isExpanded && (
                    <tr className="pm-detail">
                      <td colSpan="9" style={{ padding: 0 }}>
                        <div className="pm-detail-inner">
                          <div className="pm-detail-head">
                            <span className="pm-detail-symbol">{normalizeSymbol(symbol)}</span>
                            <span className="pm-detail-net">
                              Net <strong className={data.netQuantity >= 0 ? '' : ''} style={{ color: data.netQuantity >= 0 ? 'var(--pm-success)' : 'var(--pm-danger)' }}>
                                {data.netQuantity > 0 ? '+' : ''}{formatNumber(data.netQuantity, 2)}
                              </strong>
                              {' '}across <strong>{data.userCount}</strong> user{data.userCount !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {Object.keys(breakdown).length === 0 ? (
                            <div className="pm-empty" style={{ padding: '1rem' }}>
                              <p>No user data available</p>
                            </div>
                          ) : (
                            Object.entries(breakdown).map(([userId, userData]) => {
                              const userOrdersForSymbol = (userOrders[userId] || []).filter(
                                o => o.symbol === symbol
                              );

                              const displayId = userId.length > 8 ? `...${userId.slice(-8)}` : userId;

                              return (
                                <div className="pm-user-row" key={userId}>
                                  <div className="pm-user-avatar">
                                    {userId.length > 2 ? userId.slice(-2).toUpperCase() : userId.toUpperCase()}
                                  </div>
                                  <div className="pm-user-info">
                                    <div className="pm-user-id">
                                      {displayId}
                                      <span className="pm-user-currency">{userData.baseCurrency}</span>
                                    </div>
                                  </div>
                                  <div className="pm-user-trades">
                                    {userData.trades.map(trade => (
                                      <div className="pm-user-trade" key={trade.tradeId}>
                                        <span className={'pm-trade-tag pm-trade-tag--' + trade.side}>
                                          {getSideLabel(trade.side)}
                                        </span>
                                        <span className="pm-trade-detail">
                                          {formatNumber(trade.quantity, 2)} @ {formatNumber(trade.entryPrice, 5)}
                                        </span>
                                        {trade.leverage && (
                                          <span className="pm-trade-detail" style={{ color: 'var(--pm-text-muted)' }}>
                                            1:{trade.leverage}x
                                          </span>
                                        )}
                                        <span style={{ display: 'flex', gap: '3px', marginLeft: 'auto' }}>
                                          <button
                                            className="pm-act"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenModifyModal({ ...trade, symbol, userId, baseCurrency: userData.baseCurrency });
                                            }}
                                            disabled={processing}
                                            title="Modify"
                                          >
                                            <Edit3 /> Mod
                                          </button>
                                          <button
                                            className="pm-act pm-act--danger"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenCloseModal({ ...trade, symbol, userId, baseCurrency: userData.baseCurrency });
                                            }}
                                            disabled={processing}
                                            title="Close"
                                          >
                                            <TrendingDown /> Close
                                          </button>
                                        </span>
                                      </div>
                                    ))}
                                    {/* Pending orders for this user+symbol */}
                                    {userOrdersForSymbol.map(order => (
                                      <div className="pm-user-trade" key={order.orderId} style={{ opacity: 0.7 }}>
                                        <span className="pm-trade-tag" style={{ background: 'var(--pm-warning-light)', color: '#d97706' }}>
                                          {getSideLabel(order.side)}
                                        </span>
                                        <span className="pm-trade-detail" style={{ color: 'var(--pm-warning)' }}>
                                          {order.orderType || 'ORDER'} {formatNumber(order.quantity, 2)} @ {formatNumber(order.price, 5)}
                                        </span>
                                        <span className="pm-trade-detail" style={{ color: 'var(--pm-text-muted)', fontSize: '0.6rem' }}>
                                          {order.status}
                                        </span>
                                        <button
                                          className="pm-act pm-act--danger"
                                          style={{ marginLeft: 'auto' }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveOrder({ ...order, userId });
                                          }}
                                          disabled={processing}
                                          title="Cancel order"
                                        >
                                          <XCircle /> Cancel
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PositionsTable;
